import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const EarlyAccessSection = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/early-access`, { email });
      
      if (response.data.success) {
        setIsSuccess(true);
        setEmail("");
        toast.success(response.data.message);
      }
    } catch (error) {
      console.error("Signup error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section 
      id="early-access"
      data-testid="early-access-section"
      className="py-20 md:py-28 px-6 md:px-12 relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#FDFBF7] to-[#E0F7FA]/20" />
      <motion.div
        className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full bg-[#F58227]/10 blur-3xl"
        animate={{ scale: [1, 1.2, 1] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="max-w-xl mx-auto relative text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-3 bg-[#FCEBDC] px-6 py-3 rounded-full mb-6">
            <Sparkles size={24} className="text-[#F58227]" />
            <span className="text-base font-medium text-[#27272A]">Coming soon</span>
          </div>

          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#27272A] mb-4">
            Join the beta
          </h2>
          
          <p className="text-[#52525B] mb-8">
            Want a nudge when it's ready? No spam — just important updates.
          </p>

          {!isSuccess ? (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="early-access-email-input"
                className="flex-1 h-12 bg-white border-2 border-stone-200 rounded-full px-6 text-base focus:border-[#F58227] focus:ring-4 focus:ring-[#F58227]/10 transition-all outline-none placeholder:text-stone-400"
                disabled={isLoading}
              />
              <button
                type="submit"
                disabled={isLoading}
                data-testid="early-access-submit-button"
                className="h-12 bg-[#F58227] text-white hover:bg-[#D96D19] rounded-full px-8 font-bold shadow-lg hover:shadow-[#F58227]/30 transition-all active:scale-95 disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                  />
                ) : (
                  <>
                    <span>Notify me</span>
                    <Send size={16} />
                  </>
                )}
              </button>
            </form>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              data-testid="early-access-success"
              className="bg-[#E0F7FA] rounded-2xl p-6"
            >
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#0CBCBF] flex items-center justify-center">
                <Check size={24} className="text-white" />
              </div>
              <p className="font-semibold text-[#27272A]">You're on the list!</p>
              <p className="text-sm text-[#52525B]">We'll reach out when there's something worth sharing.</p>
            </motion.div>
          )}

          <p className="mt-4 text-xs text-[#A1A1AA]">
            Your inbox is sacred. Unsubscribe anytime.
          </p>
        </motion.div>
      </div>
    </section>
  );
};
